import React, { useRef, useState, useEffect } from 'react'
import moment from 'moment'
import classnames from 'classnames/bind'
import logger from '@adc/logger'
import { AutomationTarget } from '../../../types'
import { getCssColorFromColorName } from '../../../utils/colors'
import { ColorNames } from '../../../constants/colorVars'
import Text from '../text'
import AnimatedProgressBar from '../animatedProgressBar'
import useElementDimensions from '../../../hooks/useElementDimensions'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const PROGRESS_BAR_HEIGHT = 10
const PROGRESS_BAR_NUDGE = PROGRESS_BAR_HEIGHT / 2

interface TickProps {
  color: string
  left: number
}
const Tick = ({ color, left }: TickProps) => (
  <div style={{ backgroundColor: color, left }} className={cx('tick')} />
)

interface ProgressTimelineProps extends AutomationTarget {
  leftLabel: string
  rightLabel: string
  accentColor: ColorNames
  markerLabel: (days: number) => string
  startDate: Date
  endDate: Date
  endedLabel?: string
  className?: string
  style?: React.CSSProperties
}

const ProgressTimeline: React.FC<ProgressTimelineProps> = ({
  leftLabel,
  rightLabel,
  accentColor = ColorNames.Info,
  markerLabel,
  startDate,
  endDate,
  endedLabel = 'Ended',
  className,
  style,
}) => {
  const [markerLabelLeft, setMarkerLabelLeft] = useState(0)
  const [markerTickLeft, setMarkerTickLeft] = useState(0)
  const markerRef = useRef<HTMLDivElement>(null)
  const markerContainerRef = useRef<HTMLDivElement>(null)
  const daysLeft = Math.ceil(moment(endDate).diff(moment(), 'days', true))
  const datesAreInvalid = isNaN(daysLeft) || moment(startDate).isAfter(endDate)
  const isCompleted = moment().isSameOrAfter(endDate) && !datesAreInvalid

  if (datesAreInvalid) {
    logger.error(
      `Invalid dates provided to ProgressTimeline: startDate=${moment(
        startDate
      ).format('MMMM Do, YYYY')}, endDate=${moment(endDate).format(
        'MMMM Do, YYYY'
      )}`
    )
  }

  // eslint-disable-next-line no-nested-ternary
  const percent = datesAreInvalid
    ? 0
    : moment().isAfter(endDate)
    ? 100
    : Math.min(
        100,
        (moment().diff(startDate, 'minutes') /
          moment(endDate).diff(startDate, 'minutes')) *
          100
      )
  const formattedEndDate = moment(endDate).format('MM/DD')

  const markerContainerDimensions = useElementDimensions(markerContainerRef)
  const markerDimensions = useElementDimensions(markerRef)

  const _markerLabel = (days: number) => {
    if (moment().isBefore(startDate))
      return `Starts ${moment(startDate).format('MM/DD')}`
    return markerLabel(days)
  }

  useEffect(() => {
    if (markerDimensions && markerContainerDimensions) {
      setMarkerLabelLeft(
        Math.max(
          0,
          Math.min(
            markerContainerDimensions.width - markerDimensions.width - 3,
            (markerContainerDimensions.width * percent) / 100 -
              markerDimensions.width / 2 -
              PROGRESS_BAR_NUDGE // accounts for the end "ball" of the progress bar
          )
        )
      )
      setMarkerTickLeft(
        Math.max(
          0,
          (markerContainerDimensions.width * percent) / 100 - PROGRESS_BAR_NUDGE
        )
      )
    }
  }, [percent, markerDimensions, markerContainerDimensions])

  const backgroundColor = getCssColorFromColorName(accentColor)
  const endDateColor = getCssColorFromColorName(ColorNames.Success)

  return (
    <div
      className={cx('progress-timeline', className)}
      style={style}
      data-elm-id='progress_timeline'
    >
      <div className={cx('labels')}>
        <Text.Body as='div' size='small' weight='heavy' color={ColorNames.Gray}>
          {leftLabel}
        </Text.Body>
        <Text.Body
          as='div'
          size='small'
          weight='heavy'
          color={ColorNames.Gray}
          style={{ textAlign: 'right' }}
        >
          {rightLabel}
        </Text.Body>
      </div>
      <div className={cx('progress-bar')}>
        <AnimatedProgressBar color={accentColor} percent={percent} rounded />
        <div className={cx('dot', { completed: isCompleted })} />
      </div>
      {datesAreInvalid ? null : (
        <div className={cx('markers')}>
          <div ref={markerContainerRef} className={cx('left')}>
            {percent !== 100 && (
              <>
                <Tick color={backgroundColor} left={markerTickLeft} />
                <Text.Body
                  as='div'
                  size='extraSmall'
                  weight='heavy'
                  color={ColorNames.Gray}
                  style={{ left: markerLabelLeft }}
                  className={cx('marker-label')}
                  ref={markerRef}
                >
                  {_markerLabel(daysLeft)}
                </Text.Body>
              </>
            )}
          </div>
          <div className={cx('right')}>
            <Tick color={endDateColor} left={4} />
            <Text.Body
              className={cx('end-date')}
              as='div'
              size='extraSmall'
              weight='heavy'
              color={ColorNames.Gray}
              style={{
                right: percent < 85 || percent === 100 ? 0 : 'initial',
              }}
            >
              {percent === 100 ? endedLabel : formattedEndDate}
            </Text.Body>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProgressTimeline
